import React from "react";
import { ArrowRight } from "lucide-react";
import { useModals } from "@/lib/modal-context";

/**
 * CategoryHero — lightweight SEO hero for category landing pages.
 * Uses a proper single <h1> and descriptive alt text.
 */
export default function CategoryHero({ eyebrow, h1, sub, image, imageAlt, primaryCta = "GET FREE ESTIMATE", estimatorType }) {
    const { openConnect, openEstimator } = useModals();

    return (
        <section className="bg-[#FAFAF5]">
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-5 pb-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-7 relative overflow-hidden rounded-[28px] min-h-[420px] lg:min-h-[520px]">
                        <img
                            src={image}
                            alt={imageAlt}
                            fetchPriority="high"
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{ background: "linear-gradient(110deg, rgba(250,250,245,0.94) 0%, rgba(250,250,245,0.72) 30%, rgba(250,250,245,0.25) 52%, rgba(250,250,245,0) 70%)" }}
                        />
                        <div className="relative z-10 flex h-full flex-col justify-between p-7 sm:p-9 lg:p-11">
                            <div className="max-w-[38rem] fade-up">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-3 py-1 text-[10px] font-bold tracking-[0.15em] text-black/80 border border-black/10">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#7e9a00]" />
                                    {eyebrow}
                                </div>
                                <h1 className="mt-4 font-display text-[32px] leading-[1.08] sm:text-[40px] lg:text-[46px] font-extrabold text-[#111] tracking-[-0.03em]">
                                    {h1}
                                </h1>
                                <p className="mt-5 text-[14px] text-[#3a3a3a] max-w-[30rem] leading-[1.6] font-medium">
                                    {sub}
                                </p>
                            </div>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <button
                                    onClick={() => openEstimator(estimatorType)}
                                    data-testid="category-hero-estimate"
                                    className="btn-pill btn-lime pl-5 pr-1 py-1.5"
                                >
                                    <span className="text-[12px] font-bold tracking-[0.1em]">{primaryCta}</span>
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </span>
                                </button>
                                <button
                                    onClick={() => openConnect({ source: `${estimatorType}_page_hero` })}
                                    data-testid="category-hero-connect"
                                    className="btn-pill btn-outline-dark pl-5 pr-1 py-1.5"
                                >
                                    <span className="text-[12px] font-bold tracking-[0.1em]">CONNECT IN 15 SECS</span>
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right — quick trust badges */}
                    <div className="lg:col-span-5 flex flex-col gap-4">
                        <div className="rounded-[24px] bg-white border border-black/10 p-6">
                            <h2 className="font-display text-lg font-bold text-black tracking-[-0.02em]">Why 45DaySpaces</h2>
                            <ul className="mt-4 space-y-3 text-[14px] text-black/70 font-medium">
                                <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#7e9a00] shrink-0" /> Transparent pricing in AED, no surprise additions.</li>
                                <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#7e9a00] shrink-0" /> 45-day execution plan for standard scopes.</li>
                                <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#7e9a00] shrink-0" /> Design, procurement, execution & handover — one team.</li>
                                <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#7e9a00] shrink-0" /> WhatsApp updates across every project stage.</li>
                            </ul>
                        </div>
                        <div className="rounded-[24px] bg-[#111] p-6 text-white">
                            <div className="text-[#CCFF00] text-[10px] font-bold tracking-[0.2em]">SERVING DUBAI &amp; UAE</div>
                            <p className="mt-2 text-[14px] leading-relaxed text-white/85">
                                From Business Bay to Downtown, JLT to Marina — 45DaySpaces designs and delivers premium spaces across Dubai and the wider UAE.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
